import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  Card,
  CardContent,
  SxProps,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridFilterItem,
} from '@mui/x-data-grid';
import CommDataGrid from '../components/CommDataGrid';
import buildQuery from 'odata-query';
import { getAllMessages } from '../api/requests/aiSummaryMessages';
import { formatFullDateTime } from '../utils/utils';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { colors } from '../utils/constants';

interface MessageType {
  readonly MessageId: number;
  readonly MessageText: string;
  readonly CreatedOn: string | null;
  readonly SenderName: string;
  readonly RecipientCount: number;
  readonly IsGroup: boolean;
  readonly GroupName: string | null;
  id?: number;
}

interface DateRangeType {
  fromDate?: string;
  toDate?: string;
}

interface DynamicFilter {
  id: number;
  field: string;
  operator: string;
  value: any;
  fromDate?: string;
  toDate?: string;
}

interface LocationState {
  from?: string;
  filter?: string;
}

const AllMessages: React.FC = () => {
  const location = useLocation();
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [open, setOpen] = useState<boolean>(false);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const prevFilterModel = useRef<GridFilterModel>(filterModel);
  const hasFetchedInitially = useRef<boolean>(false);
  const isMounting = useRef<boolean>(true);

  const fields = [
    { value: 'MessageText', label: 'Message Text', operators: ['contains'] },
    { value: 'SenderName', label: 'Sender Name', operators: ['contains'] },
    { value: 'GroupName', label: 'Group Name', operators: ['contains'] },
    {
      value: 'MessageType',
      label: 'Message Type',
      operators: ['equals', 'doesNotEqual'],
      options: [
        'Text',
        'Unknown',
        'Image',
        'ImageWithText',
        'Video',
        'VideoWithText',
        'Audio',
        'AudioWithText',
        'Document',
        'DocumentWithText',
        'Media',
        'MediaWithText',
        'Link',
        'LinkWithText',
      ],
    },
    {
      value: 'RecipientCount',
      label: 'Recipient Count',
      operators: ['equals'],
    },
    {
      value: 'CreatedOn',
      label: 'Created On',
      operators: ['is', 'between'],
    },
  ];

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = paginationModel.page;
      const pageSize = paginationModel.pageSize;
      const skip = page * pageSize;
      const top = pageSize;
      let filter: string | undefined;
      const filterParts: string[] = [];
      if (filterModel.items.length > 0) {
        for (let i = 0; i < filterModel.items.length; i++) {
          const { field, operator, value } = filterModel.items[i];
          if (
            (value === undefined ||
              value === null ||
              (typeof value === 'string' && value.trim() === '')) &&
            operator !== 'between'
          ) {
            continue;
          }
          const operatorMap: { [key: string]: string } = {
            is: 'eq',
            equals: 'eq',
            doesNotEqual: 'ne',
          };

          switch (field) {
            case 'MessageText':
            case 'SenderName':
            case 'GroupName':
              if (operator === 'contains') {
                const lowercaseValue = String(value).toLowerCase().trim();
                if (lowercaseValue) {
                  filterParts.push(
                    `contains(tolower(${field}),'${lowercaseValue}')`
                  );
                }
              }
              break;
            case 'MessageType':
              if (['equals', 'doesNotEqual'].includes(operator)) {
                const lowercaseValue = String(value).toLowerCase().trim();
                if (lowercaseValue) {
                  const odataOperator = operatorMap[operator] || 'eq';
                  filterParts.push(
                    `tolower(${field}) ${odataOperator} '${lowercaseValue}'`
                  );
                }
              }
              break;
            case 'RecipientCount':
              if (operator === 'equals') {
                const numericValue = Number(value);
                if (!isNaN(numericValue)) {
                  filterParts.push(`${field} eq ${numericValue}`);
                } else {
                  setError('Invalid number for Recipient Count');
                }
              }
              break;
            case 'CreatedOn':
              if (operator === 'is' && typeof value === 'string') {
                const date = dayjs(value, 'YYYY-MM-DD', true);
                if (date.isValid()) {
                  const fromFormatted = date
                    .startOf('day')
                    .toISOString()
                    .replace(/\.\d{3}Z$/, 'Z');
                  const toFormatted = date
                    .endOf('day')
                    .toISOString()
                    .replace(/\.\d{3}Z$/, 'Z');
                  filterParts.push(
                    `(${field} ge ${fromFormatted} and ${field} le ${toFormatted})`
                  );
                } else {
                  setError('Invalid date for Created On. Use YYYY-MM-DD.');
                  continue;
                }
              } else if (
                operator === 'between' &&
                typeof value === 'object' &&
                'fromDate' in value &&
                'toDate' in value
              ) {
                const { fromDate, toDate } = value as DateRangeType;
                if (
                  typeof fromDate === 'string' &&
                  typeof toDate === 'string'
                ) {
                  const fromDateObj = new Date(`${fromDate}.000Z`);
                  const toDateObj = new Date(`${toDate}.000Z`);
                  if (
                    !isNaN(fromDateObj.getTime()) &&
                    !isNaN(toDateObj.getTime())
                  ) {
                    const fromFormatted = fromDateObj
                      .toISOString()
                      .replace(/\.\d{3}Z$/, 'Z');
                    const toFormatted = toDateObj
                      .toISOString()
                      .replace(/\.\d{3}Z$/, 'Z');
                    filterParts.push(
                      `(${field} ge ${fromFormatted} and ${field} le ${toFormatted})`
                    );
                  } else {
                    setError('Invalid date range for Created On');
                    continue;
                  }
                } else {
                  setError('Invalid date range for Created On');
                  continue;
                }
              }
              break;
            default:
              continue;
          }
        }
        if (filterParts.length > 0) {
          filter = filterParts.join(' and ');
        } else {
          filter = undefined;
        }
      }
      const query = buildQuery({
        skip,
        top,
        count: true,
        filter,
        orderBy: 'CreatedOn desc',
      });
      const response = await getAllMessages(query);
      if (response.data.value) {
        const mappedRows = response.data.value
          .map((row: MessageType) => ({
            ...row,
            id: row.MessageId,
            CreatedOn:
              row.CreatedOn &&
              moment(row.CreatedOn, moment.ISO_8601, true).isValid()
                ? row.CreatedOn
                : null,
          }))
          .filter((row): row is MessageType & { id: number } => row !== null);
        setMessages(mappedRows);
        setTotalRowsCount(response.data['@odata.count'] || 0);
      } else {
        setMessages([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Fetch error:', axiosError.message);
      setError('Failed to load messages.');
      setMessages([]);
      setTotalRowsCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel]);

  const handleFilterChange = (
    id: number,
    key: keyof DynamicFilter,
    value: any
  ) => {
    setDynamicFilters((prev: DynamicFilter[]) =>
      prev.map((filter: DynamicFilter) =>
        filter.id === id ? { ...filter, [key]: value } : filter
      )
    );
  };

  const addFilter = () => {
    setDynamicFilters((prev: DynamicFilter[]) => [
      ...prev,
      { id: Date.now(), field: '', operator: '', value: '' },
    ]);
  };

  const removeFilter = (id: number) => {
    setDynamicFilters((prev: DynamicFilter[]) =>
      prev.filter((filter: DynamicFilter) => filter.id !== id)
    );
  };

  const applyDynamicFilters = () => {
    const newFilterItems: GridFilterItem[] = [];
    const errors: string[] = [];
    for (let i = 0; i < dynamicFilters.length; i++) {
      const { field, operator, value, fromDate, toDate } = dynamicFilters[i];
      if (!field || !operator) {
        errors.push('Field and operator required');
        continue;
      }

      if (field === 'CreatedOn') {
        if (operator === 'between') {
          if (!fromDate || !toDate) {
            errors.push('From and To dates required');
            continue;
          }
          const fromDayjs = dayjs(fromDate, 'YYYY-MM-DD', true);
          const toDayjs = dayjs(toDate, 'YYYY-MM-DD', true);
          if (
            fromDayjs.isValid() &&
            toDayjs.isValid() &&
            !toDayjs.isBefore(fromDayjs)
          ) {
            const fromFormatted = fromDayjs
              .startOf('day')
              .subtract(5.5, 'hour')
              .format('YYYY-MM-DDTHH:mm:ss');
            const toFormatted = toDayjs
              .endOf('day')
              .subtract(5.5, 'hour')
              .format('YYYY-MM-DDTHH:mm:ss');
            newFilterItems.push({
              field,
              operator,
              value: { fromDate: fromFormatted, toDate: toFormatted },
            });
          } else {
            errors.push('Invalid date range for Created On');
            continue;
          }
        } else if (operator === 'is') {
          if (!value) {
            errors.push('Date required for Created On');
            continue;
          }
          const date = dayjs(value, 'YYYY-MM-DD', true);
          if (date.isValid()) {
            newFilterItems.push({
              field,
              operator,
              value: date.format('YYYY-MM-DD'),
            });
          } else {
            errors.push('Invalid date for Created On');
            continue;
          }
        }
      } else if (field === 'RecipientCount' && operator === 'equals') {
        const trimmedValue = String(value).trim();
        if (!trimmedValue || isNaN(Number(trimmedValue))) {
          errors.push('Invalid number for Recipient Count');
          continue;
        }
        newFilterItems.push({ field, operator, value: trimmedValue });
      } else if (
        field === 'MessageType' &&
        ['equals', 'doesNotEqual'].includes(operator)
      ) {
        const trimmedValue = String(value).trim();
        if (!trimmedValue) {
          errors.push('Message Type value required');
          continue;
        }
        newFilterItems.push({ field, operator, value: trimmedValue });
      } else if (
        ['MessageText', 'SenderName', 'GroupName'].includes(field) &&
        operator === 'contains'
      ) {
        const trimmedValue = String(value).trim();
        if (!trimmedValue) {
          errors.push(`${field} value required`);
          continue;
        }
        newFilterItems.push({ field, operator, value: trimmedValue });
      } else {
        continue;
      }
    }

    setError(errors.length > 0 ? errors.join('; ') : null);
    // Reset pagination to page 0 when applying new filters
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilterModel({ items: newFilterItems });
  };

  const clearFilters = () => {
    setDynamicFilters([]);
    setFilterModel({ items: [] });
    setError(null);
    // Reset pagination to page 0 when clearing filters
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setOpen(false);
  };

  const toggleFilterPanel = () => {
    setOpen((prev: boolean) => !prev);
  };

  // Handle filter model changes from CommDataGrid
  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0) {
      return;
    }
    const supportedOperators = [
      'contains',
      'equals',
      'doesNotEqual',
      'is',
      'between',
    ];
    const isValid = newFilterModel.items.every((item) =>
      supportedOperators.includes(item.operator)
    );
    if (isValid && newFilterModel.items.length === filterModel.items.length) {
      setFilterModel(newFilterModel);
    }
  };

  // Log filterModel changes
  useEffect(() => {
    if (
      JSON.stringify(prevFilterModel.current) !== JSON.stringify(filterModel)
    ) {
      prevFilterModel.current = filterModel;
    }
  }, [filterModel]);

  // Initial render: Set filters based on location.state only on first mount
  useEffect(() => {
    if (!isMounting.current) {
      return;
    }

    const state = location.state as LocationState | undefined;
    if (state?.from && ['Text', 'Multimedia'].includes(state.filter || '')) {
      const operator = state.filter === 'Text' ? 'equals' : 'doesNotEqual';
      const newFilterItems: GridFilterItem[] = [
        { field: 'MessageType', operator, value: 'Text' },
      ];
      const newDynamicFilters: DynamicFilter[] = [
        { id: Date.now(), field: 'MessageType', operator, value: 'Text' },
      ];
      setFilterModel({ items: newFilterItems });
      setDynamicFilters(newDynamicFilters);
    } else {
      setFilterModel({ items: [] });
      setDynamicFilters([]);
    }

    if (!hasFetchedInitially.current) {
      hasFetchedInitially.current = true;
      fetchMessages();
    }
    return () => {};
  }, [location]);

  // Mark mounting phase as complete after first render
  useEffect(() => {
    isMounting.current = false;
  }, []);

  // Fetch messages when pagination or filters change, after initial render
  useEffect(() => {
    if (!isMounting.current && hasFetchedInitially.current) {
      fetchMessages();
    }
  }, [paginationModel, filterModel, fetchMessages]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'MessageType',
        headerName: 'Message Type',
        flex: 0.8,
        filterable: false,
        renderCell: ({ value }) => {
          const displayValue = value ?? '--';
          const transformedValue =
            typeof displayValue == 'string' && displayValue != '--'
              ? displayValue.includes('With')
                ? displayValue.split('With').join(' With ')
                : displayValue
              : displayValue;
          return (
            <Tooltip title={transformedValue} arrow>
              <Typography
                variant='body2'
                sx={{ pt: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {transformedValue}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'MessageText',
        headerName: 'Message Text',
        flex: 1,
        filterable: false,
        renderCell: ({ value }) => (
          <Tooltip title={value || '--'} arrow>
            <Typography
              variant='body2'
              sx={{ pt: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value || '--'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'CreatedOn',
        headerName: 'Created On',
        flex: 1,
        filterable: false,
        valueGetter: ({ value }) =>
          value && moment(value, moment.ISO_8601, true).isValid()
            ? moment(value).toDate()
            : null,
        renderCell: ({ row }) => (
          <Typography variant='body2' sx={{ pt: 2 }}>
            {row.CreatedOn ? formatFullDateTime(row.CreatedOn) : '--'}
          </Typography>
        ),
      },
      {
        field: 'SenderName',
        headerName: 'Sender Name',
        flex: 1,
        filterable: false,
        renderCell: ({ value }) => (
          <Tooltip title={value || '--'} arrow>
            <Typography
              variant='body2'
              sx={{ pt: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value || '--'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'RecipientCount',
        headerName: 'Recipient Count',
        flex: 0.8,
        filterable: false,
        renderCell: ({ value }) => (
          <Typography variant='body2' sx={{ pt: 2 }}>
            {value ?? 0}
          </Typography>
        ),
      },
      {
        field: 'GroupName',
        headerName: 'Group Name',
        flex: 1,
        filterable: false,
        renderCell: ({ value }) => (
          <Tooltip title={value || '--'} arrow>
            <Typography
              variant='body2'
              sx={{ pt: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value || '--'}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const memoizedRows = useMemo(() => messages, [messages]);

  const isApplyDisabled = dynamicFilters.some((filter: DynamicFilter) => {
    if (!filter.field || !filter.operator) return true;
    if (filter.field === 'CreatedOn' && filter.operator === 'between')
      return !filter.fromDate || !filter.toDate;
    if (filter.field === 'CreatedOn' && filter.operator === 'is')
      return !filter.value;
    return !filter.value && filter.value !== 0;
  });

  const title = filterModel.items.some(
    (item) =>
      item.field === 'MessageType' &&
      item.operator === 'equals' &&
      item.value === 'Text'
  )
    ? 'All Text Messages'
    : filterModel.items.some(
        (item) =>
          item.field === 'MessageType' &&
          item.operator === 'doesNotEqual' &&
          item.value === 'Text'
      )
    ? 'All Multimedia Messages'
    : 'All Messages';

  const cardPosition = {
    top: buttonRef.current
      ? (buttonRef.current.offsetTop || 0) +
        (buttonRef.current.offsetHeight || 0)
      : 0,
    left: buttonRef.current ? buttonRef.current.offsetLeft || 0 : 0,
  };

  const renderFilterButton = () => (
    <Button
      ref={buttonRef}
      variant='outlined'
      startIcon={<FilterListIcon />}
      onClick={toggleFilterPanel}
      sx={outlineButtonStyles}
    >
      {open ? 'Hide Filters' : 'Show Filters'}
    </Button>
  );

  return (
    <Box>
      {open && (
        <Card
          sx={{
            position: 'absolute',
            top: cardPosition.top,
            left: cardPosition.left,
            width: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <CardContent>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Filters
            </Typography>
            {dynamicFilters.length === 0 ? (
              <Typography variant='body2' sx={{ mb: 2 }}>
                No filters applied. Click "Add Filter" to start.
              </Typography>
            ) : (
              dynamicFilters.map((filter) => {
                const selectedField = fields.find(
                  (f) => f.value === filter.field
                );
                const operators = selectedField?.operators || [];
                const options = selectedField?.options || [];

                return (
                  <Box
                    key={filter.id}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'center',
                    }}
                  >
                    <FormControl sx={{ width: '190px' }}>
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={filter.field}
                        label='Field'
                        onChange={(e) =>
                          handleFilterChange(filter.id, 'field', e.target.value)
                        }
                      >
                        <MenuItem value=''>Select Field</MenuItem>
                        {fields.map((field) => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {filter.field && (
                      <FormControl sx={{ width: '150px' }}>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={filter.operator}
                          label='Operator'
                          onChange={(e) =>
                            handleFilterChange(
                              filter.id,
                              'operator',
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value=''>Select Operator</MenuItem>
                          {operators.map((op) => (
                            <MenuItem key={op} value={op}>
                              {op === 'equals'
                                ? 'Equals'
                                : op === 'doesNotEqual'
                                ? 'Does Not Equal'
                                : op === 'contains'
                                ? 'Contains'
                                : op === 'between'
                                ? 'Between'
                                : op === 'is'
                                ? 'Is'
                                : op}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {filter.field && filter.operator && (
                      <>
                        {filter.field === 'CreatedOn' &&
                        filter.operator === 'between' ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              label='From'
                              type='date'
                              value={filter.fromDate || ''}
                              onChange={(e) =>
                                handleFilterChange(
                                  filter.id,
                                  'fromDate',
                                  e.target.value
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: '150px' }}
                            />
                            <TextField
                              label='To'
                              type='date'
                              value={filter.toDate || ''}
                              onChange={(e) =>
                                handleFilterChange(
                                  filter.id,
                                  'toDate',
                                  e.target.value
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: '150px' }}
                            />
                          </Box>
                        ) : filter.field === 'CreatedOn' &&
                          filter.operator === 'is' ? (
                          <TextField
                            label='Date'
                            type='date'
                            value={filter.value || ''}
                            onChange={(e) =>
                              handleFilterChange(
                                filter.id,
                                'value',
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: '150px' }}
                          />
                        ) : filter.field === 'MessageType' ? (
                          <FormControl sx={{ width: '150px' }}>
                            <InputLabel>Value</InputLabel>
                            <Select
                              value={filter.value}
                              label='Value'
                              onChange={(e) =>
                                handleFilterChange(
                                  filter.id,
                                  'value',
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value=''>Select Value</MenuItem>
                              {options.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option.includes('With')
                                    ? option.split('With').join(' With ')
                                    : option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : filter.field === 'RecipientCount' ? (
                          <TextField
                            label='Value'
                            type='number'
                            value={filter.value}
                            onChange={(e) =>
                              handleFilterChange(
                                filter.id,
                                'value',
                                e.target.value
                              )
                            }
                            sx={{ width: '150px' }}
                          />
                        ) : (
                          <TextField
                            label='Value'
                            value={filter.value}
                            onChange={(e) =>
                              handleFilterChange(
                                filter.id,
                                'value',
                                e.target.value
                              )
                            }
                            sx={{ width: '150px' }}
                          />
                        )}
                      </>
                    )}

                    <IconButton
                      onClick={() => removeFilter(filter.id)}
                      color='error'
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                );
              })
            )}

            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={addFilter}
              sx={{ ...outlineButtonStyles, mb: 2 }}
            >
              Add Filter
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                sx={applyFilterButtonStyles}
                onClick={applyDynamicFilters}
                disabled={isApplyDisabled}
              >
                Apply Filters
              </Button>
              <Button sx={outlineButtonStyles} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {error && (
        <Typography color='error' sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <CommDataGrid
        title={title}
        description='View and manage all messages in the system'
        rows={memoizedRows}
        columns={columns}
        section='All Messages'
        setPaginationModel={setPaginationModel}
        paginationModel={paginationModel}
        totalRowsCount={totalRowsCount}
        filterMode='server'
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        loading={loading}
        renderButtons={renderFilterButton}
        disableColumnFilter={true}
      />
    </Box>
  );
};

export default AllMessages;

const outlineButtonStyles: SxProps = {
  border: `1px solid ${colors.indigoBlueColor}`,
  textTransform: 'inherit',
  borderRadius: '8px',
  color: colors.indigoBlueColor,
  padding: '4px 16px',
};

const applyFilterButtonStyles: SxProps = {
  backgroundColor: colors.indigoBlueColor,
  textTransform: 'inherit',
  borderRadius: '8px',
  padding: '4px 16px',
  color: colors.white,
  '&.Mui-disabled': {
    color: colors.spanishGrayColor,
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
};
